import { useState } from "react";
import type { SortDir, SortKey } from "@/utils/courseSort";
import { getTypeLabel } from "@/utils/courseType";

interface FilterSortBarProps {
  regions: string[];
  typeCodes: string[];
  region: string;
  typeCode: string;
  sortKey: SortKey;
  sortDir: SortDir;
  onRegionChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onSortKeyChange: (value: SortKey) => void;
  onSortDirChange: (value: SortDir) => void;
}

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "default", label: "기본 순서" },
  { value: "tuition", label: "수강비" },
  { value: "satisfaction", label: "만족도" },
  { value: "employment3m", label: "취업률 3개월" },
];

const selectClass =
  "h-11 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-gray-950";

export default function FilterSortBar({
  regions,
  typeCodes,
  region,
  typeCode,
  sortKey,
  sortDir,
  onRegionChange,
  onTypeChange,
  onSortKeyChange,
  onSortDirChange,
}: FilterSortBarProps) {
  const [open, setOpen] = useState(false);

  const regionActive = region !== "전체";
  const typeActive = typeCode !== "전체";
  const filterCount = (regionActive ? 1 : 0) + (typeActive ? 1 : 0);
  const sortActive = sortKey !== "default";

  const filterGroup = (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="지역 필터"
        value={region}
        onChange={(e) => onRegionChange(e.target.value)}
        className={selectClass}
      >
        <option value="전체">전체 지역</option>
        {regions.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>

      <select
        aria-label="훈련유형 필터"
        value={typeCode}
        onChange={(e) => onTypeChange(e.target.value)}
        className={selectClass}
      >
        <option value="전체">전체 유형</option>
        {typeCodes.map((t) => (
          <option key={t} value={t}>
            {getTypeLabel(t)}
          </option>
        ))}
      </select>
    </div>
  );

  const sortGroup = (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="정렬 기준"
        value={sortKey}
        onChange={(e) => onSortKeyChange(e.target.value as SortKey)}
        className={selectClass}
      >
        {SORT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <div className="inline-flex h-11 items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-700">
        <button
          type="button"
          onClick={() => onSortDirChange("asc")}
          aria-pressed={sortDir === "asc"}
          className={`flex h-full items-center gap-1 px-3 text-sm whitespace-nowrap transition-colors ${focusRing} ${
            sortDir === "asc"
              ? "bg-brand-700 text-white"
              : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
        >
          <i className="ri-arrow-up-line" />
          오름
        </button>
        <button
          type="button"
          onClick={() => onSortDirChange("desc")}
          aria-pressed={sortDir === "desc"}
          className={`flex h-full items-center gap-1 px-3 text-sm whitespace-nowrap transition-colors ${focusRing} ${
            sortDir === "desc"
              ? "bg-brand-700 text-white"
              : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
        >
          <i className="ri-arrow-down-line" />
          내림
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`md:hidden flex h-11 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 ${focusRing}`}
      >
        <span className="flex items-center gap-2">
          <i className="ri-equalizer-line text-gray-500 dark:text-gray-400" />
          <span>필터·정렬</span>
          {filterCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-700 px-1.5 text-xs font-medium text-white">
              {filterCount}
            </span>
          )}
          {sortActive && (
            <span className="flex h-5 items-center rounded-full bg-amber-100 px-1.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              정렬
            </span>
          )}
        </span>
        <i
          className={`text-gray-500 dark:text-gray-400 ${
            open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
          }`}
        />
      </button>

      <div className="hidden md:flex md:flex-row md:items-center md:justify-between md:gap-3">
        {filterGroup}
        {sortGroup}
      </div>

      {open && (
        <div className="mt-3 flex flex-col gap-3 md:hidden">
          {filterGroup}
          {sortGroup}
        </div>
      )}
    </div>
  );
}